import { FC } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { UsageBo } from '@utils/api/back-office/usage/use-get-usage-bo.tsx';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

interface OrganizationCostChartProps {
  data: UsageBo[];
}

// Create a new type that represents the transformed data,
// where `total_cost` is now a number and we add a `shortName` property.
interface ExtendedUsageBo extends Omit<UsageBo, 'total_cost'> {
  total_cost: number;
  shortName: string;
}

const OrganizationCostChart: FC<OrganizationCostChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  // Sort the data by total_cost (converted to a number)
  const sortedData = [...data].sort(
    (a, b) => parseFloat(b.total_cost) - parseFloat(a.total_cost)
  );

  // Map the sorted data to include a shortened organization name
  // and convert total_cost to a number with two decimals.
  const formattedData: ExtendedUsageBo[] = sortedData.map(item => ({
    ...item,
    shortName:
      item.organization_name.length > 15
        ? `${item.organization_name.substring(0, 12)}...`
        : item.organization_name,
    total_cost: parseFloat(parseFloat(item.total_cost).toFixed(2))
  }));

  return (
    <div className="w-full h-full flex flex-col">
      <h2
        className="text-base font-semibold mb-4"
        style={{ color: getColour('normalText') }}
      >
        {t('organization-costs')}
      </h2>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%" minHeight={350}>
          <BarChart
            data={formattedData}
            margin={{ top: 10, right: 20, left: 10, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="shortName"
              angle={-45}
              textAnchor="end"
              height={80}
              interval={0}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              label={{
                value: t('cost-eur'),
                angle: -90,
                position: 'insideLeft',
                offset: 0
              }}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={value => {
                if (typeof value === 'number') {
                  return [
                    new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'EUR',
                      minimumFractionDigits: 2
                    }).format(value),
                    t('total-cost')
                  ];
                }
                return [String(value), t('total-cost')];
              }}
              labelFormatter={(value, payload) => {
                const item = payload?.find(p => p.payload?.organization_name);
                return item?.payload?.organization_name || value;
              }}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ paddingTop: '10px' }} />
            <Bar
              dataKey="total_cost"
              // eslint-disable-next-line no-restricted-syntax -- Semantic chart color
              fill="#3b82f6"
              name="Total Cost (EUR)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OrganizationCostChart;
